import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styles from "../../styles/shared/other-coins-signal/Watchlists.module.css";
import { FaBookmark, FaCalendarAlt, FaTag, FaTh } from "react-icons/fa";
import { FaCircleArrowLeft, FaCircleArrowRight } from "react-icons/fa6";
import UseFetchWatchlists from "../../hooks/api/useFetchWatchlists";
import {
  WEEKLY_CORE_ID,
  WEEKLY_TACTICAL_ID,
  parseWatchlistCoins,
} from "./watchlistCoins";

function Watchlists({
  assets = [],
  setSelectedWatchlist,
  selectedWatchlist,
  weeklyPortfolio = { core: [], tactical: [] },
}) {
  const {
    exchangeState: { coinCategories },
    newInsightState: { watchlists: personalWatchlists },
  } = useSelector((state) => state);

  // Fetch user watchlists
  UseFetchWatchlists();

  const watchlists = Object.keys(coinCategories);
  const scrollContainerRef = useRef(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: true });

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScroll({
        left: scrollLeft > 0,
        right: scrollLeft < scrollWidth - clientWidth - 1,
      });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const handleScroll = () => checkScrollPosition();
      container.addEventListener("scroll", handleScroll);
      checkScrollPosition();
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [
    watchlists.length,
    personalWatchlists?.length,
    weeklyPortfolio?.core?.length,
    weeklyPortfolio?.tactical?.length,
  ]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -200,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 200,
        behavior: "smooth",
      });
    }
  };

  const selectWatchlist = (watchlist) => () => {
    setSelectedWatchlist(watchlist);
  };

  const weeklyChips = [
    { id: WEEKLY_CORE_ID, label: "Weekly Core", coins: weeklyPortfolio?.core },
    {
      id: WEEKLY_TACTICAL_ID,
      label: "Weekly Tactical",
      coins: weeklyPortfolio?.tactical,
    },
  ].filter((chip) => chip.coins?.length);

  const categoryTone = {
    DEFI: styles.toneDefi,
    MEME: styles.toneMeme,
    PRIVACY: styles.tonePrivacy,
  };
  const spareTones = [styles.toneTeal, styles.toneIndigo, styles.toneOrange, styles.toneSlate];
  let spareIndex = 0;

  if (!watchlists.length && !personalWatchlists?.length && !weeklyChips.length) {
    return null;
  }

  return (
    <div className={styles.watchListWrapper}>
      {canScroll?.left && (
        <button
          className={`${styles.scrollButton} ${!canScroll.left ? styles.disabled : ""} ${styles.leftScroll}`}
          onClick={scrollLeft}
          type="button"
          disabled={!canScroll.left}
        >
          <FaCircleArrowLeft size={16} />
        </button>
      )}

      <div className={styles.watchListContainer} ref={scrollContainerRef}>
        <div
          className={`${styles.watchlistItem} ${selectedWatchlist === "all" ? styles.active : ""}`}
          onClick={selectWatchlist("all")}
        >
          <FaTh className={styles.chipIcon} />
          All <span className={styles.coinCounter}>{assets.length}</span>
        </div>
        {personalWatchlists?.length > 0 &&
          personalWatchlists?.map((personalWatchlist, index) => (
            <div
              key={`personal-${index}`}
              className={`${styles.watchlistItem} ${styles.tonePersonal} ${selectedWatchlist === `personal-${index}` ? styles.active : ""}`}
              onClick={selectWatchlist(`personal-${index}`)}
            >
              <FaBookmark className={styles.chipIcon} />
              Personal
              <span className={styles.coinCounter}>
                ({parseWatchlistCoins(personalWatchlist.list_of_coins).length})
              </span>
            </div>
          ))}
        {watchlists?.map((watchlist, index) => {
          const tone =
            categoryTone[String(watchlist).toUpperCase()] ||
            spareTones[spareIndex++ % spareTones.length];
          return (
            <div
              key={index}
              className={`${styles.watchlistItem} ${tone} ${selectedWatchlist === watchlist ? styles.active : ""}`}
              onClick={selectWatchlist(watchlist)}
            >
              <FaTag className={styles.chipIcon} />
              {watchlist}
              <span className={styles.coinCounter}>
                ({coinCategories?.[watchlist]?.length})
              </span>
            </div>
          );
        })}
        {weeklyChips.map((chip) => (
          <div
            key={chip.id}
            className={`${styles.watchlistItem} ${styles.toneWeekly} ${selectedWatchlist === chip.id ? styles.active : ""}`}
            onClick={selectWatchlist(chip.id)}
          >
            <FaCalendarAlt className={styles.chipIcon} />
            {chip.label}
            <span className={styles.coinCounter}>({chip.coins.length})</span>
          </div>
        ))}
      </div>
      {canScroll?.right && (
        <button
          className={`${styles.scrollButton} ${!canScroll.right ? styles.disabled : ""} ${styles.rightScroll}`}
          onClick={scrollRight}
          type="button"
          disabled={!canScroll.right}
        >
          <FaCircleArrowRight size={16} />
        </button>
      )}
    </div>
  );
}

export default Watchlists;
